'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import ForgotPasswordForm from './ForgotPasswordForm';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';

type AuthView = 'login' | 'register' | 'forgot';

export default function AuthScreen({ defaultView = 'login' }: { defaultView?: AuthView }) {
  const [view, setView] = useState<AuthView>(defaultView);

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left panel — brand */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 relative overflow-hidden p-12">
        <div className="absolute inset-0 gradient-bg opacity-90" />
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-56 h-56 bg-pink-300/20 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10">
          <Link href="/" className="flex items-center gap-3 cursor-pointer">
            <AppLogo size={40} />
            <span className="text-2xl font-bold text-white">Uyir</span>
          </Link>
        </div>
        <div className="relative z-10">
          <blockquote className="text-white/90 text-3xl font-bold leading-snug mb-6">
            "Whatever you're feeling,
            <br />
            you can say it here."
          </blockquote>
          <p className="text-white/60 text-lg">
            A safe space to express, reflect, and heal.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-3">
          <div className="flex -space-x-2">
            {['A', 'P', 'R', 'S'].map((letter, i) => (
              <div
                key={`avatar-${letter}-${i}`}
                className="w-8 h-8 rounded-full bg-white/20 border-2 border-white/30 flex items-center justify-center text-white text-xs font-bold"
              >
                {letter}
              </div>
            ))}
          </div>
          <p className="text-white/70 text-sm">
            2,400+ people already talking
          </p>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-12 lg:px-16 py-12">
        {/* Mobile logo */}
        <div className="flex items-center gap-2 mb-8 lg:hidden">
          <Link href="/" className="flex items-center gap-2 cursor-pointer">
            <AppLogo size={32} />
            <span className="text-xl font-bold gradient-text">Uyir</span>
          </Link>
        </div>

        <div className="w-full max-w-md">
          {view === 'login' && (
            <LoginForm
              onSwitchToRegister={() => setView('register')}
              onSwitchToForgot={() => setView('forgot')}
            />
          )}
          {view === 'register' && (
            <RegisterForm onSwitchToLogin={() => setView('login')} />
          )}
          {view === 'forgot' && (
            <ForgotPasswordForm onBack={() => setView('login')} />
          )}
        </div>
      </div>
    </div>
  );
}